import React, { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Trophy, Gift, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { MilestoneTier, REVENUE_MILESTONES } from '../../types/sellerIntegration';

interface MilestoneRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestoneId: string;
  totalRevenue: number;
  currency: string;
  onClaim: (milestoneId: string) => Promise<void>;
  isClaiming?: boolean;
}

export const MilestoneRewardModal: React.FC<MilestoneRewardModalProps> = ({
  isOpen,
  onClose,
  milestoneId,
  totalRevenue,
  currency,
  onClaim,
  isClaiming = false,
}) => {
  const milestone = REVENUE_MILESTONES.find(m => m.id === milestoneId) || REVENUE_MILESTONES[0];

  // Disparar confetes ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      // Disparo inicial
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#0FFF55', '#00F0FF', '#FFB800', '#FF2E93', '#FFFFFF'],
      });

      // Disparos laterais tipo canhão
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;

      const interval: any = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({
          particleCount,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#0FFF55', '#0dd149', '#FFD700'],
        });
        confetti({
          particleCount,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#00F0FF', '#0FFF55', '#FFB800'],
        });
      }, 350);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const formatMoney = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency === 'COP' ? 'COP' : 'BRL',
      maximumFractionDigits: 0,
    }).format(val);
  };

  // Próxima meta recomendada após resgatar
  const nextMilestone = REVENUE_MILESTONES.find(m => m.target > milestone.target) || {
    label: '2.5M',
    target: 2500000
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#16181d] border border-[#0FFF55]/40 text-white max-w-md p-0 overflow-hidden shadow-2xl rounded-3xl">
        {/* Glow Background Top */}
        <div className="relative bg-gradient-to-b from-[#0FFF55]/20 via-[#16181d]/80 to-[#16181d] p-8 text-center pt-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-[#0FFF55]/30 blur-3xl pointer-events-none rounded-full" />
          
          {/* Caixa de presente com animação */}
          <div className="relative inline-block mb-4">
            <div className="w-24 h-24 bg-gradient-to-tr from-[#0dd149] via-[#0FFF55] to-[#8aff95] rounded-3xl flex items-center justify-center shadow-lg shadow-[#0FFF55]/40 animate-bounce mx-auto">
              <Gift className="w-12 h-12 text-[#121316]" />
            </div>
            <div className="absolute -top-2 -right-2 bg-yellow-400 text-[#121316] p-1.5 rounded-full font-black shadow-md">
              <Sparkles className="w-5 h-5 text-[#121316]" />
            </div>
          </div>

          <DialogHeader className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0FFF55]/20 border border-[#0FFF55]/40 rounded-full text-xs font-bold text-[#0FFF55] mx-auto uppercase tracking-wider">
              <Trophy className="w-3.5 h-3.5" />
              Meta Conquistada!
            </div>
            <DialogTitle className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              {milestone.rewardTitle}
            </DialogTitle>
            <DialogDescription className="text-gray-300 text-sm leading-relaxed max-w-xs mx-auto">
              {milestone.rewardDescription}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Card de Faturamento Atual */}
        <div className="px-6 pb-6 space-y-4">
          <div className="bg-[#1f2329]/70 border border-gray-700/60 rounded-2xl p-4 flex items-center justify-between">
            <div className="text-left">
              <span className="text-xs text-gray-400 font-medium block">Faturamento em Outubro</span>
              <span className="text-2xl font-black text-[#0FFF55]">
                {formatMoney(totalRevenue)}
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-400 font-medium block">Nível Alcançado</span>
              <span className="text-sm font-bold text-white bg-white/10 px-2.5 py-1 rounded-lg">
                {milestone.badge}
              </span>
            </div>
          </div>

          {/* Banner de Próxima Meta */}
          <div className="p-3 bg-[#0FFF55]/10 border border-[#0FFF55]/30 rounded-xl flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#0FFF55] flex-shrink-0" />
            <p className="text-xs text-gray-200">
              Ao resgatar, sua régua avançará automaticamente para o próximo marco de <strong className="text-[#0FFF55]">{nextMilestone.label}</strong>!
            </p>
          </div>

          {/* Botão de Resgate */}
          <Button
            onClick={() => onClaim(milestone.id)}
            disabled={isClaiming}
            className="w-full bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black text-base py-6 rounded-2xl shadow-xl shadow-[#0FFF55]/25 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            {isClaiming ? 'Resgatando Conquista...' : `Resgatar e Subir para Meta de ${nextMilestone.label}`}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
